import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Eraser, Save, Trash2, Download, Palette } from "lucide-react";

export const Route = createFileRoute("/teacher/whiteboard")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "Whiteboard · MAKAI Teachers" }] }),
  component: Page,
});

const COLORS = ["#0B1736", "#C8A03C", "#D32F2F", "#1976D2", "#2E7D32", "#F9A825"];

function Page() {
  const ctx = useTeacherContext();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [color, setColor] = useState("#0B1736");
  const [size, setSize] = useState(3);
  const [title, setTitle] = useState("");
  const [sessions, setSessions] = useState<any[]>([]);

  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx2 = c.getContext("2d"); if (!ctx2) return;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * 2; c.height = rect.height * 2;
    ctx2.scale(2, 2);
    ctx2.fillStyle = "#ffffff";
    ctx2.fillRect(0, 0, rect.width, rect.height);
    ctx2.lineCap = "round"; ctx2.lineJoin = "round";
  }, []);

  useEffect(() => {
    if (!ctx.staffId) return;
    supabase.from("whiteboard_sessions").select("id, title, image_data, created_at")
      .eq("teacher_id", ctx.staffId).order("created_at", { ascending: false }).limit(8)
      .then(({ data }) => setSessions(data ?? []));
  }, [ctx.staffId]);

  function pos(e: React.PointerEvent<HTMLCanvasElement>) {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }
  function start(e: React.PointerEvent<HTMLCanvasElement>) {
    drawing.current = true;
    const c = canvasRef.current!.getContext("2d")!;
    const p = pos(e);
    c.strokeStyle = color; c.lineWidth = size;
    c.beginPath(); c.moveTo(p.x, p.y);
  }
  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const c = canvasRef.current!.getContext("2d")!;
    const p = pos(e); c.lineTo(p.x, p.y); c.stroke();
  }
  function end() { drawing.current = false; }

  function clear() {
    const c = canvasRef.current!; const x = c.getContext("2d")!;
    x.fillStyle = "#ffffff"; x.fillRect(0, 0, c.width, c.height);
  }

  async function save() {
    if (!ctx.staffId || !ctx.schoolId) { toast.error("Teacher profile not ready"); return; }
    const dataUrl = canvasRef.current!.toDataURL("image/png");
    const { data, error } = await supabase.from("whiteboard_sessions").insert({
      school_id: ctx.schoolId, teacher_id: ctx.staffId,
      title: title || `Whiteboard ${new Date().toLocaleString()}`,
      image_data: dataUrl,
    }).select("id, title, image_data, created_at").single();
    if (error) return toast.error(error.message);
    setSessions((s) => [data, ...s].slice(0, 8));
    toast.success("Whiteboard saved");
  }

  function download() {
    const url = canvasRef.current!.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url; a.download = `${title || "whiteboard"}.png`; a.click();
  }

  return (
    <AppShell role="teacher" title="Digital Whiteboard" subtitle="Sketch, explain, save — share with your class">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-card p-3 shadow-soft">
          <Input className="max-w-xs" placeholder="Whiteboard title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <div className="flex items-center gap-1 rounded-lg border border-border px-2 py-1">
            <Palette className="h-4 w-4 text-muted-foreground" />
            {COLORS.map((c) => (
              <button key={c} onClick={() => setColor(c)} aria-label={c}
                className={`h-6 w-6 rounded-full border ${color === c ? "ring-2 ring-primary" : ""}`} style={{ background: c }} />
            ))}
            <button onClick={() => setColor("#ffffff")} aria-label="Eraser" className="ml-1 grid h-6 w-6 place-items-center rounded-full border bg-background">
              <Eraser className="h-3.5 w-3.5" />
            </button>
          </div>
          <input type="range" min={1} max={20} value={size} onChange={(e) => setSize(+e.target.value)} className="w-32" />
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={clear}><Trash2 className="mr-1 h-3.5 w-3.5" /> Clear</Button>
            <Button size="sm" variant="outline" onClick={download}><Download className="mr-1 h-3.5 w-3.5" /> Download</Button>
            <Button size="sm" onClick={save}><Save className="mr-1 h-3.5 w-3.5" /> Save</Button>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-white shadow-soft">
          <canvas ref={canvasRef}
            onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end}
            className="h-[60vh] w-full touch-none rounded-2xl" style={{ background: "#fff" }} />
        </div>

        {sessions.length > 0 && (
          <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
            <div className="mb-3 text-sm font-semibold">Recent boards</div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {sessions.map((s) => (
                <div key={s.id} className="rounded-xl border border-border bg-background/60 p-2">
                  <img loading="lazy" decoding="async" src={s.image_data} alt={s.title} className="aspect-[4/3] w-full rounded-lg object-contain" />
                  <div className="mt-2 truncate text-xs font-semibold">{s.title}</div>
                  <div className="text-[11px] text-muted-foreground">{new Date(s.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
